import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouterState } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../../../hooks/useAuth"
import { useCompanyApi } from "../../../hooks/useCompanyApi"
import { useCompanyMutation, useCompanyQuery } from "../../../hooks/useCompanyQuery"
import {
  fetchNotificationCount,
  fetchNotifications,
  fetchPlatformNotificationCount,
  fetchPlatformNotifications,
  markAllNotificationsRead,
  markAllPlatformNotificationsRead,
  markNotificationRead,
  markPlatformNotificationRead,
} from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { queryKeys } from "../../../lib/query-keys"
import { useTenant } from "../../../lib/tenant"
import {
  type INotificationLike,
  meetingIdFromPath,
  sectionNotifications,
  unreadReadyIdsForMeeting,
} from "./dedupeNotifications"
import { NotificationBellView } from "./NotificationBellView"

const PLATFORM_NOTIFICATIONS_KEY = ["platform-notifications"] as const
const PLATFORM_NOTIFICATION_COUNT_KEY = ["platform-notification-count"] as const
const EMPTY_NOTIFICATIONS: INotificationLike[] = []

interface INotificationBellProps {
  /** Super-admins and org owners/admins only — the backend gates the
   * notification routes on exactly this bar, so anyone else polling would just
   * collect a 403 every minute. */
  canView: boolean
}

function invalidatePlatform(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: PLATFORM_NOTIFICATIONS_KEY })
  void queryClient.invalidateQueries({ queryKey: PLATFORM_NOTIFICATION_COUNT_KEY })
}

export function NotificationBell({ canView }: INotificationBellProps): React.JSX.Element | null {
  const company = useCompanyApi()
  const { user } = useAuth()
  const tenant = useTenant()
  const queryClient = useQueryClient()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [opened, setOpened] = useState(false)
  const sentReadIds = useRef<Set<number>>(new Set())
  const previousUnread = useRef<number | null>(null)

  const platformMode = tenant.isAdmin && user?.isAdmin === true && !user.activeOrganizationId
  const meetingId = meetingIdFromPath(pathname)
  const listWanted = opened || meetingId !== null
  const companyEnabled = canView && !platformMode

  const companyCount = useCompanyQuery({
    key: queryKeys.notificationCount,
    queryFn: (client) => fetchNotificationCount(client),
    refetchInterval: 60_000,
    enabled: companyEnabled,
  })

  const companyList = useCompanyQuery({
    key: queryKeys.notifications,
    queryFn: (client) => fetchNotifications(client),
    refetchInterval: 60_000,
    enabled: companyEnabled && listWanted,
  })

  const platformCount = useQuery({
    queryKey: PLATFORM_NOTIFICATION_COUNT_KEY,
    queryFn: () => fetchPlatformNotificationCount(),
    refetchInterval: 60_000,
    enabled: platformMode,
  })

  const platformList = useQuery({
    queryKey: PLATFORM_NOTIFICATIONS_KEY,
    queryFn: () => fetchPlatformNotifications(),
    refetchInterval: 60_000,
    enabled: platformMode && listWanted,
  })

  const companyMark = useCompanyMutation({
    mutationFn: (client, id: number) => markNotificationRead(client, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationCount() })
    },
  })

  const companyMarkAll = useCompanyMutation({
    mutationFn: (client) => markAllNotificationsRead(client),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationCount() })
    },
  })

  const platformMark = useMutation({
    mutationFn: (id: number) => markPlatformNotificationRead(id),
    onSuccess: () => {
      invalidatePlatform(queryClient)
    },
  })

  const platformMarkAll = useMutation({
    mutationFn: () => markAllPlatformNotificationsRead(),
    onSuccess: () => {
      invalidatePlatform(queryClient)
    },
  })

  const rows = platformMode ? (platformList.data ?? EMPTY_NOTIFICATIONS) : (companyList.data ?? EMPTY_NOTIFICATIONS)
  const unreadValue = platformMode ? platformCount.data?.unread : companyCount.data?.unread
  const unread = unreadValue ?? 0
  const loading = platformMode ? platformList.isLoading : companyList.isLoading
  const queryError = platformMode ? platformList.error : companyList.error
  const errorMessage = queryError ? extractErrorMessage(queryError) : null
  const sections = useMemo(() => sectionNotifications(rows, new Date()), [rows])

  const markRead = useCallback(
    (id: number): void => {
      const onError = (): void => {
        sentReadIds.current.delete(id)
      }
      if (platformMode) {
        platformMark.mutate(id, { onError })
        return
      }
      companyMark.mutate(id, { onError })
    },
    [platformMode, platformMark, companyMark],
  )

  useEffect(() => {
    if (unreadValue === undefined) return
    const previous = previousUnread.current
    previousUnread.current = unreadValue
    if (!meetingId || previous === null || unreadValue <= previous) return
    const key = platformMode ? PLATFORM_NOTIFICATIONS_KEY : queryKeys.notifications()
    void queryClient.invalidateQueries({ queryKey: key })
  }, [meetingId, unreadValue, platformMode, queryClient])

  useEffect(() => {
    if (!meetingId) return
    const pending = unreadReadyIdsForMeeting(rows, meetingId).filter((id) => !sentReadIds.current.has(id))
    if (pending.length === 0) return
    for (const id of pending) {
      sentReadIds.current.add(id)
      markRead(id)
    }
  }, [meetingId, rows, markRead])

  const handleOpen = useCallback((): void => {
    setOpened(true)
  }, [])

  const handleClose = useCallback((): void => {
    setOpened(false)
  }, [])

  const handleMarkRead = useCallback(
    (ids: number[]): void => {
      for (const id of ids) markRead(id)
    },
    [markRead],
  )

  const handleMarkAll = useCallback((): void => {
    if (platformMode) {
      platformMarkAll.mutate()
      return
    }
    companyMarkAll.mutate()
  }, [platformMode, platformMarkAll, companyMarkAll])

  if (!platformMode && (!company || !canView)) return null

  return (
    <NotificationBellView
      unread={unread}
      opened={opened}
      loading={loading}
      sections={sections}
      showModelIds={platformMode}
      markingAll={platformMode ? platformMarkAll.isPending : companyMarkAll.isPending}
      errorMessage={errorMessage}
      onOpen={handleOpen}
      onClose={handleClose}
      onMarkRead={handleMarkRead}
      onMarkAllRead={handleMarkAll}
    />
  )
}
